import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { imageUrl } from "@/lib/api";
import ProductCard from "@/components/ProductCard";
import { ShieldCheck, Sparkles, Wrench, Headphones, ArrowRight, Cpu, Monitor, HardDrive, Keyboard, Router, Printer, Package, MonitorCheck } from "lucide-react";

const catIcon = {
  "Laptops": Cpu,
  "Desktop Computers": Monitor,
  "Monitors": MonitorCheck,
  "Computer Accessories": Keyboard,
  "Networking Equipment": Router,
  "Printers": Printer,
  "Storage Devices": HardDrive,
  "Other Equipment": Package,
};

export default function Home() {
  const [featured, setFeatured] = useState([]);
  const [recent, setRecent] = useState([]);
  const [categories, setCategories] = useState([]);

  useEffect(() => {
    (async () => {
      try {
        const [f, r, c] = await Promise.all([
          api.get("/products/featured", { params: { limit: 6 } }),
          api.get("/products/recent", { params: { limit: 4 } }),
          api.get("/categories"),
        ]);
        setFeatured(f.data);
        setRecent(r.data);
        setCategories(c.data);
      } catch (e) { console.error(e); }
    })();
  }, []);

  const heroSpotlight = featured[0];

  return (
    <div>
      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 bg-grid opacity-60" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-14 pb-20">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-stretch">
            <div className="lg:col-span-7 flex flex-col justify-center animate-fade-up">
              <span className="inline-flex items-center gap-2 self-start px-3 py-1.5 rounded-full bg-white border border-slate-200 text-xs font-semibold tracking-wider uppercase text-slate-600 shadow-sm">
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" /> Tested · Inspected · Warrantied
              </span>
              <h1 className="mt-6 font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight text-slate-900 leading-[1.05]">
                Refurbished business laptops<br />
                <span className="text-slate-500">without the retail markup.</span>
              </h1>
              <p className="mt-6 text-base sm:text-lg text-slate-600 max-w-xl leading-relaxed">
                Mymi Global Business sources, inspects, and resells premium used laptops, desktops,
                monitors, and computer equipment. Fair prices. Real warranties. No surprises.
              </p>
              <div className="mt-8 flex flex-wrap items-center gap-3">
                <Link
                  to="/inventory"
                  data-testid="hero-shop-cta"
                  className="inline-flex items-center gap-2 px-5 py-3 rounded-full bg-slate-900 text-white text-sm font-semibold hover:bg-blue-600 transition-colors"
                >
                  Shop inventory <ArrowRight className="w-4 h-4" />
                </Link>
                <Link
                  to="/contact"
                  data-testid="hero-contact-cta"
                  className="inline-flex items-center gap-2 px-5 py-3 rounded-full bg-white border border-slate-200 text-slate-900 text-sm font-semibold hover:border-slate-300"
                >
                  Contact us
                </Link>
              </div>
              <div className="mt-10 grid grid-cols-3 gap-4 max-w-lg">
                <Stat label="Point inspection" value="50+" />
                <Stat label="Limited warranty" value="90d" />
                <Stat label="Off retail" value="Up to 60%" />
              </div>
            </div>

            <div className="lg:col-span-5 grid grid-cols-6 grid-rows-6 gap-3 min-h-[420px]">
              {heroSpotlight ? (
                <Link
                  to={`/product/${heroSpotlight.id}`}
                  data-testid="hero-spotlight"
                  className="col-span-6 row-span-4 rounded-2xl overflow-hidden border border-slate-200 bg-white relative group card-lift"
                >
                  <img src={imageUrl(heroSpotlight.images?.[0])} alt={heroSpotlight.name} className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                  <div className="absolute inset-0 bg-gradient-to-t from-slate-900/90 via-slate-900/20 to-transparent" />
                  <div className="absolute bottom-0 left-0 right-0 p-5 text-white">
                    <div className="text-[11px] uppercase tracking-wider font-mono opacity-80">Spotlight · {heroSpotlight.brand}</div>
                    <div className="font-display text-xl font-semibold mt-1 line-clamp-1">{heroSpotlight.name}</div>
                    <div className="mt-2 flex items-center gap-3 text-sm">
                      <span className="font-semibold">${heroSpotlight.price.toFixed(2)}</span>
                      <span className="opacity-80">·</span>
                      <span className="opacity-90">{heroSpotlight.condition}</span>
                    </div>
                  </div>
                </Link>
              ) : (
                <div className="col-span-6 row-span-4 rounded-2xl bg-white border border-slate-200 grid place-items-center text-slate-400 text-sm">Loading spotlight…</div>
              )}
              <div className="col-span-3 row-span-2 rounded-2xl bg-slate-900 text-white p-5 flex flex-col justify-between">
                <ShieldCheck className="w-6 h-6 text-blue-400" />
                <div>
                  <div className="font-display font-semibold">Every unit warrantied</div>
                  <div className="text-xs text-slate-300 mt-1">90-day limited coverage on core hardware.</div>
                </div>
              </div>
              <div className="col-span-3 row-span-2 rounded-2xl bg-blue-600 text-white p-5 flex flex-col justify-between">
                <Wrench className="w-6 h-6" />
                <div>
                  <div className="font-display font-semibold">50-point inspection</div>
                  <div className="text-xs text-blue-100 mt-1">Diagnostics, battery health, cleaning.</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* TRUST STRIP */}
      <section className="border-y border-slate-200 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 grid grid-cols-2 md:grid-cols-4 gap-6">
          <Trust icon={ShieldCheck} title="Quality Used Equipment" desc="Business-class brands only." />
          <Trust icon={Wrench} title="Tested & Inspected" desc="50-point diagnostic before listing." />
          <Trust icon={Sparkles} title="Competitive Prices" desc="Fair pricing, transparent grades." />
          <Trust icon={Headphones} title="Customer Support" desc="Real humans answer questions." />
        </div>
      </section>

      {/* FEATURED */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="flex items-end justify-between mb-8">
          <div>
            <div className="text-xs font-semibold uppercase tracking-wider text-blue-600">Featured</div>
            <h2 className="mt-2 font-display text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight">Handpicked deals</h2>
          </div>
          <Link to="/inventory" className="text-sm font-medium text-slate-600 hover:text-slate-900" data-testid="featured-see-all">
            See all →
          </Link>
        </div>
        {featured.length ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="featured-grid">
            {featured.map(p => <ProductCard key={p.id} product={p} />)}
          </div>
        ) : (
          <div className="text-slate-500 text-sm">No featured products yet.</div>
        )}
      </section>

      {/* CATEGORIES */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8">
          <div className="text-xs font-semibold uppercase tracking-wider text-blue-600">Browse</div>
          <h2 className="mt-2 font-display text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight">Shop by category</h2>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {categories.map(c => {
            const Icon = catIcon[c.name] || Package;
            return (
              <Link
                key={c.id}
                to={`/inventory?category=${encodeURIComponent(c.name)}`}
                data-testid={`category-tile-${c.slug}`}
                className="card-lift group rounded-2xl bg-white border border-slate-200 p-5 flex flex-col gap-4 hover:border-slate-900"
              >
                <span className="w-10 h-10 rounded-lg bg-slate-900 text-white grid place-items-center group-hover:bg-blue-600 transition-colors">
                  <Icon className="w-5 h-5" />
                </span>
                <div>
                  <div className="font-display font-semibold text-slate-900">{c.name}</div>
                  <div className="text-xs text-slate-500 mt-0.5">Explore →</div>
                </div>
              </Link>
            );
          })}
        </div>
      </section>

      {/* RECENT */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="flex items-end justify-between mb-8">
          <div>
            <div className="text-xs font-semibold uppercase tracking-wider text-blue-600">Just in</div>
            <h2 className="mt-2 font-display text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight">Recently added</h2>
          </div>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5" data-testid="recent-grid">
          {recent.map(p => <ProductCard key={p.id} product={p} />)}
        </div>
      </section>
    </div>
  );
}

function Stat({ value, label }) {
  return (
    <div>
      <div className="font-display text-2xl font-bold text-slate-900">{value}</div>
      <div className="text-xs text-slate-500 mt-1">{label}</div>
    </div>
  );
}
function Trust({ icon: Icon, title, desc }) {
  return (
    <div className="flex items-start gap-3">
      <span className="w-10 h-10 rounded-lg bg-slate-100 grid place-items-center flex-shrink-0">
        <Icon className="w-5 h-5 text-slate-900" />
      </span>
      <div>
        <div className="font-display font-semibold text-slate-900">{title}</div>
        <div className="text-xs text-slate-500 mt-0.5">{desc}</div>
      </div>
    </div>
  );
}
